// Form Import/Export Service
// Handles exporting forms with settings and importing them to different accounts

import { ref, get, set } from 'firebase/database';
import { realTimeDb } from '../firebase';

class FormImportExportService {
  
  /**
   * Export a single form with all its settings
   * @param {string} userId - Current user ID
   * @param {string} formId - Form ID to export
   * @returns {Object} - Exportable form data
   */
  async exportForm(userId, formId) {
    try {
      const formRef = ref(realTimeDb, `users/${userId}/forms/${formId}`);
      const snapshot = await get(formRef);
      
      if (!snapshot.exists()) {
        throw new Error('Form not found');
      }
      
      const formData = snapshot.val();
      
      // Create export package
      const exportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        type: 'single-form',
        form: {
          // Basic form properties
          title: formData.title,
          formType: formData.formType,
          selectedStyle: formData.selectedStyle,
          
          // Form structure
          fields: formData.fields || [],
          variants: formData.variants || [],
          
          // Settings
          webhookUrl: formData.webhookUrl,
          showReferenceNumber: formData.showReferenceNumber,
          requireAllFields: formData.requireAllFields,
          
          // Access settings
          isPublic: formData.isPublic,
          accessPasswordHash: formData.accessPasswordHash, // Note: passwords will need to be reset
          
          // Payment settings
          paymentSettings: formData.paymentSettings,
          
          // Styling and branding
          clientEmail: formData.clientEmail,
          
          // Advanced settings
          customCss: formData.customCss,
          customJs: formData.customJs,
          
          // Exclude system fields
          // createdAt, signature, and user-specific data are not exported
        }
      };
      
      return exportData;
    } catch (error) {
      console.error('Error exporting form:', error);
      throw error;
    }
  }
  
  /**
   * Export all forms for a user
   * @param {string} userId - User ID
   * @returns {Object} - Exportable forms data
   */
  async exportAllForms(userId) {
    try {
      const formsRef = ref(realTimeDb, `users/${userId}/forms`);
      const snapshot = await get(formsRef);
      
      if (!snapshot.exists()) {
        throw new Error('No forms found');
      }
      
      const formsData = snapshot.val();
      const forms = [];
      
      for (const [formId, formData] of Object.entries(formsData)) {
        try {
          const exportedForm = await this.exportForm(userId, formId);
          forms.push(exportedForm.form);
        } catch (error) {
          console.error(`Error exporting form ${formId}:`, error);
        }
      }
      
      const exportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        type: 'all-forms',
        formsCount: forms.length,
        forms: forms
      };
      
      return exportData;
    } catch (error) {
      console.error('Error exporting all forms:', error);
      throw error;
    }
  }
  
  /**
   * Export user profile settings (excluding sensitive data)
   * @param {string} userId - User ID
   * @returns {Object} - Exportable profile data
   */
  async exportProfileSettings(userId) {
    try {
      const profileRef = ref(realTimeDb, `users/${userId}/profile`);
      const snapshot = await get(profileRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      const profileData = snapshot.val();
      
      // Export only non-sensitive profile settings
      const exportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        type: 'profile-settings',
        profile: {
          clientEmail: profileData.clientEmail,
          defaultSettings: {
            showReferenceNumber: profileData.showReferenceNumber,
            requireAllFields: profileData.requireAllFields,
            selectedStyle: profileData.selectedStyle
          }
          // Exclude plan, payment, and subscription data
        }
      };
      
      return exportData;
    } catch (error) {
      console.error('Error exporting profile settings:', error);
      throw error;
    }
  }
  
  /**
   * Create a complete export package
   * @param {string} userId - User ID
   * @returns {Object} - Complete export package
   */
  async createCompleteExport(userId) {
    try {
      const [formsExport, profileExport] = await Promise.all([
        this.exportAllForms(userId),
        this.exportProfileSettings(userId)
      ]);
      
      const completeExport = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        type: 'complete-package',
        package: {
          forms: formsExport,
          profile: profileExport
        }
      };
      
      return completeExport;
    } catch (error) {
      console.error('Error creating complete export:', error);
      throw error;
    }
  }
  
  /**
   * Import a form into the current user's account
   * @param {string} userId - Target user ID
   * @param {Object} formData - Form data to import
   * @param {Object} options - Import options
   * @returns {string} - New form ID
   */
  async importForm(userId, formData, options = {}) {
    try {
      const {
        replacePasswords = true,
        generateNewSignature = true,
        preserveTitle = false
      } = options;
      
      // Generate new form ID
      const newFormId = 'imported-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      
      // Prepare form for import
      const importedForm = {
        ...formData,
        createdAt: new Date().toISOString(),
        signature: generateNewSignature ? 
          Math.floor(10000000 + Math.random() * 90000000) : 
          formData.signature,
        title: preserveTitle ? formData.title : `${formData.title} (Imported)`,
        isImported: true,
        importedAt: new Date().toISOString()
      };
      
      // Clear password protection if requested
      if (replacePasswords) {
        importedForm.accessPasswordHash = '';
        importedForm.isPublic = true;
      }
      
      // Clear logo URL (user will need to re-upload)
      // Note: Skip logo URL as it needs to be re-uploaded
      
      // Clear payment settings (user will need to reconfigure)
      if (importedForm.paymentSettings) {
        importedForm.paymentSettings = {};
      }
      
      // Save imported form
      const formRef = ref(realTimeDb, `users/${userId}/forms/${newFormId}`);
      await set(formRef, importedForm);
      
      return newFormId;
    } catch (error) {
      console.error('Error importing form:', error);
      throw error;
    }
  }
  
  /**
   * Import multiple forms
   * @param {string} userId - Target user ID
   * @param {Array} formsData - Array of form data to import
   * @param {Object} options - Import options
   * @returns {Array} - Array of new form IDs
   */
  async importMultipleForms(userId, formsData, options = {}) {
    const importedFormIds = [];
    const errors = [];
    
    for (let i = 0; i < formsData.length; i++) {
      try {
        const formId = await this.importForm(userId, formsData[i], options);
        importedFormIds.push(formId);
      } catch (error) {
        errors.push({ index: i, error: error.message });
      }
    }
    
    return {
      successful: importedFormIds,
      errors: errors,
      totalImported: importedFormIds.length,
      totalErrors: errors.length
    };
  }
  
  /**
   * Validate import data format
   * @param {Object} importData - Data to validate
   * @returns {Object} - Validation result
   */
  validateImportData(importData) {
    const result = {
      valid: true,
      errors: [],
      warnings: []
    };
    
    try {
      // Check version
      if (!importData.version) {
        result.warnings.push('No version information found');
      }
      
      // Check type
      if (!importData.type) {
        result.errors.push('Import type not specified');
        result.valid = false;
      }
      
      // Validate based on type
      switch (importData.type) {
        case 'single-form':
          if (!importData.form) {
            result.errors.push('No form data found');
            result.valid = false;
          } else {
            this._validateFormData(importData.form, result);
          }
          break;
          
        case 'all-forms':
          if (!importData.forms || !Array.isArray(importData.forms)) {
            result.errors.push('No forms array found');
            result.valid = false;
          } else {
            importData.forms.forEach((form, index) => {
              this._validateFormData(form, result, `Form ${index + 1}: `);
            });
          }
          break;
          
        case 'complete-package':
          if (!importData.package) {
            result.errors.push('No package data found');
            result.valid = false;
          }
          break;
          
        default:
          result.errors.push(`Unknown import type: ${importData.type}`);
          result.valid = false;
      }
      
    } catch (error) {
      result.errors.push(`Validation error: ${error.message}`);
      result.valid = false;
    }
    
    return result;
  }
  
  /**
   * Validate individual form data
   * @private
   */
  _validateFormData(form, result, prefix = '') {
    if (!form.title) {
      result.warnings.push(`${prefix}Form has no title`);
    }
    
    if (!form.fields || !Array.isArray(form.fields)) {
      result.warnings.push(`${prefix}Form has no fields array`);
    }
    
    if (form.accessPasswordHash) {
      result.warnings.push(`${prefix}Form has password protection (will be cleared on import)`);
    }
    
    if (form.paymentSettings && Object.keys(form.paymentSettings).length > 0) {
      result.warnings.push(`${prefix}Form has payment settings (will need to be reconfigured)`);
    }
  }
  
  /**
   * Download data as JSON file
   * @param {Object} data - Data to download
   * @param {string} filename - File name
   */
  downloadAsJson(data, filename) {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }
}

export default new FormImportExportService();